import React from 'react';
import { createPortal } from 'react-dom';

interface PortalGuruConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    isLoading?: boolean;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'info' | 'warning' | 'success';
}

export const PortalGuruConfirmationModal: React.FC<PortalGuruConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    isLoading = false,
    confirmText = 'Konfirmasi',
    cancelText = 'Batal',
    type = 'danger'
}) => {
    if (!isOpen) return null;

    const getColorClasses = () => {
        switch (type) {
            case 'danger':
                return {
                    iconBg: 'bg-red-100',
                    iconText: 'text-red-600',
                    buttonBg: 'bg-red-600 hover:bg-red-700',
                    buttonShadow: 'shadow-red-600/20',
                    icon: 'delete_forever'
                };
            case 'info':
                return {
                    iconBg: 'bg-blue-100',
                    iconText: 'text-blue-600',
                    buttonBg: 'bg-blue-600 hover:bg-blue-700',
                    buttonShadow: 'shadow-blue-600/20',
                    icon: 'info'
                };
            case 'warning':
                return {
                    iconBg: 'bg-amber-100',
                    iconText: 'text-amber-600',
                    buttonBg: 'bg-amber-600 hover:bg-amber-700',
                    buttonShadow: 'shadow-amber-600/20',
                    icon: 'warning'
                };
            case 'success':
                return {
                    iconBg: 'bg-emerald-100',
                    iconText: 'text-emerald-600',
                    buttonBg: 'bg-emerald-600 hover:bg-emerald-700',
                    buttonShadow: 'shadow-emerald-600/20',
                    icon: 'check_circle'
                };
            default:
                return {
                    iconBg: 'bg-gray-100',
                    iconText: 'text-gray-600',
                    buttonBg: 'bg-gray-900 hover:bg-gray-800',
                    buttonShadow: 'shadow-gray-900/20',
                    icon: 'info'
                };
        }
    };

    const colors = getColorClasses();

    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-2xl transform transition-all scale-100">
                <div className="text-center">
                    <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${colors.iconBg} ${colors.iconText}`}>
                        <span className="material-symbols-outlined text-[28px]">
                            {colors.icon}
                        </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                        {message}
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-colors disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`flex-1 px-4 py-2.5 ${colors.buttonBg} text-white rounded-xl font-bold text-sm shadow-lg ${colors.buttonShadow} transition-all flex items-center justify-center gap-2 disabled:opacity-70`}
                    >
                        {isLoading && (
                            <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>
                        )}
                        {isLoading ? 'Memproses...' : confirmText}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
